import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle, TrendingDown, ArrowLeft, DollarSign } from 'lucide-react';
import { getPortfolio } from './api';
import { TIER_STYLES } from './constants';

const TIER_ORDER = ['Highest Confidence', 'Watch', 'Imminent', 'Critical'];

export default function Portfolio() {
    const navigate = useNavigate();
    const { data, isLoading, error } = useQuery({
        queryKey: ['profitabilityPortfolio'],
        queryFn: async () => {
            const res = await getPortfolio();
            if (!res.ok) throw new Error('Could not load portfolio');
            return res.json();
        },
        staleTime: 30_000,
    });

    if (isLoading) {
        return (
            <div className="max-w-7xl mx-auto p-6 flex items-center justify-center h-64 text-slate-500">
                <Loader2 className="animate-spin mr-2" size={18} /> Loading portfolio…
            </div>
        );
    }
    if (error) return <div className="max-w-7xl mx-auto p-6 text-red-600">{error.message}</div>;

    const { property_count = 0, tier_counts = {}, aggregate_capital = {}, critical_flags = [], variable_heatmap = {}, properties = [] } = data || {};
    const totalTiered = TIER_ORDER.reduce((sum, t) => sum + (tier_counts[t] || 0), 0);

    return (
        <div className="max-w-7xl mx-auto p-6">
            <button onClick={() => navigate('/dashboard/profitability-index')}
                    className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 mb-4 transition-colors">
                <ArrowLeft size={14} /> Back to Index
            </button>

            <header className="mb-8">
                <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">Portfolio View</h1>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    Rollup across {property_count} {property_count === 1 ? 'property' : 'properties'}. Latest saved run per property.
                </p>
            </header>

            {/* Top metric cards */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <TierDistributionCard tierCounts={tier_counts} total={totalTiered} />
                <CapitalCard label="Near-term Capital Need" value={aggregate_capital.near_term_total} icon={<DollarSign size={14} />} accent="text-red-600" />
                <CapitalCard label="20-Year Capital Need" value={aggregate_capital.long_term_total} icon={<DollarSign size={14} />} accent="text-blue-600" />
            </section>

            {/* Critical flags */}
            {critical_flags.length > 0 && (
                <section className="mb-8">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-3 flex items-center gap-2">
                        <AlertTriangle size={14} className="text-red-600" /> Critical Overrides ({critical_flags.length})
                    </h2>
                    <div className="space-y-2">
                        {critical_flags.map(f => (
                            <div key={f.id} className="p-3 rounded-lg border-l-4 border-red-600 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900">
                                <div className="font-semibold text-red-900 dark:text-red-200">{f.property_name}</div>
                                <ul className="mt-1 text-xs text-red-800 dark:text-red-300 list-disc list-inside">
                                    {f.reasons.map((r, i) => <li key={i}>{r}</li>)}
                                </ul>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Variable heatmap */}
            {Object.keys(variable_heatmap).length > 0 && (
                <section className="mb-8">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-3">
                        Portfolio-Wide Variable Impact
                    </h2>
                    <VariableHeatmap heatmap={variable_heatmap} />
                </section>
            )}

            {/* Property list */}
            <section>
                <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-3">Properties ({properties.length})</h2>
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                    <table className="w-full text-sm bg-white dark:bg-slate-900">
                        <thead className="text-[11px] uppercase tracking-wider bg-slate-50 dark:bg-slate-800/40 text-slate-500 dark:text-slate-400">
                            <tr>
                                <th className="py-2.5 px-4 text-left font-medium">Property</th>
                                <th className="py-2.5 px-4 text-left font-medium">Tier</th>
                                <th className="py-2.5 px-4 text-right font-medium">JPRO</th>
                                <th className="py-2.5 px-4 text-left font-medium">Confidence</th>
                                <th className="py-2.5 px-4 text-left font-medium">Last scored</th>
                            </tr>
                        </thead>
                        <tbody>
                            {properties.map(p => {
                                const tierStyle = TIER_STYLES[p.tier] || TIER_STYLES['Watch'];
                                return (
                                    <tr key={p.id} onClick={() => navigate(`/dashboard/profitability-index?run=${p.id}`)}
                                        className="cursor-pointer border-t border-slate-100 dark:border-slate-800 hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-colors">
                                        <td className="py-2.5 px-4">
                                            <div className="font-medium text-slate-800 dark:text-slate-100">{p.property_name || p.property_id || 'Unnamed'}</div>
                                            {p.address && <div className="text-xs text-slate-500 dark:text-slate-400">{p.address}</div>}
                                        </td>
                                        <td className="py-2.5 px-4">
                                            <span className={`px-2 py-0.5 text-xs rounded-full font-semibold ${tierStyle.bg} ${tierStyle.fg}`}>{p.tier}</span>
                                        </td>
                                        <td className="py-2.5 px-4 text-right tabular-nums font-semibold text-slate-800 dark:text-slate-100">{p.jpro ?? '—'}</td>
                                        <td className="py-2.5 px-4 text-slate-600 dark:text-slate-300">{p.confidence}</td>
                                        <td className="py-2.5 px-4 text-slate-500 dark:text-slate-400 text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                                    </tr>
                                );
                            })}
                            {properties.length === 0 && (
                                <tr><td colSpan={5} className="py-8 text-center text-slate-500 dark:text-slate-400">
                                    No properties saved yet. Run a Profitability Index and save it to see it here.
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}

function TierDistributionCard({ tierCounts, total }) {
    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-3">Tier Distribution</div>
            {total === 0 ? (
                <div className="text-sm text-slate-500 dark:text-slate-400">No saved runs</div>
            ) : (
                <>
                    <div className="flex h-3 rounded-full overflow-hidden shadow-sm">
                        {TIER_ORDER.map(tier => {
                            const count = tierCounts[tier] || 0;
                            if (count === 0) return null;
                            const pct = (count / total) * 100;
                            const style = TIER_STYLES[tier] || TIER_STYLES['Watch'];
                            return <div key={tier} className={style.bg} style={{ width: `${pct}%` }} title={`${tier}: ${count}`} />;
                        })}
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5">
                        {TIER_ORDER.map(tier => {
                            const count = tierCounts[tier] || 0;
                            const style = TIER_STYLES[tier] || TIER_STYLES['Watch'];
                            return (
                                <div key={tier} className="flex items-center gap-2 text-xs">
                                    <span className={`w-2 h-2 rounded-full ${style.bg}`} />
                                    <span className="text-slate-700 dark:text-slate-200">{tier}</span>
                                    <span className="ml-auto font-semibold text-slate-900 dark:text-white tabular-nums">{count}</span>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

function CapitalCard({ label, value, icon, accent }) {
    const display = value ? `$${value.toLocaleString()}` : '—';
    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1.5">
                <span className={accent}>{icon}</span>
                {label}
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums tracking-tight">{display}</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Summed across all properties</div>
        </div>
    );
}

function VariableHeatmap({ heatmap }) {
    const sorted = Object.entries(heatmap)
        .sort((a, b) => a[1].total_impact - b[1].total_impact)
        .slice(0, 10);

    const worstImpact = Math.min(...sorted.map(([, v]) => v.total_impact), 0);

    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800">
            {sorted.map(([key, v]) => {
                const width = worstImpact === 0 ? 0 : (v.total_impact / worstImpact) * 100;
                const isNegative = v.total_impact < 0;
                return (
                    <div key={key} className="p-3 grid grid-cols-[1fr,2fr,auto] items-center gap-3">
                        <div>
                            <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{v.label}</div>
                            {v.worst_property && (
                                <div className="text-[11px] text-slate-500 dark:text-slate-400">
                                    Worst at: <span className="font-medium text-slate-700 dark:text-slate-300">{v.worst_property}</span>
                                </div>
                            )}
                        </div>
                        <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            {isNegative && (
                                <div className="h-full bg-red-500 rounded-full transition-all" style={{ width: `${Math.abs(width)}%` }} />
                            )}
                        </div>
                        <div className={`text-sm font-semibold tabular-nums ${isNegative ? 'text-red-600 dark:text-red-400' : 'text-slate-500'}`}>
                            {v.total_impact > 0 ? '+' : ''}{v.total_impact.toFixed(1)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
